/**
 * DataPageHeader — page-header chrome shared by every Analytics
 * sub-page (`PortfolioOverview`, `PerformanceAnalytics`, `RiskAnalysis`,
 * `PositionManagement`, `TradingTools`).
 *
 * The 4 simpler sub-pages use it as-is (just title + subtitle + icon).
 * The Risk Analysis page wraps it in a flex container with a tab
 * switcher — supported via the optional `extras` slot rendered to the
 * right of the title.
 *
 * No per-chain colour palette here (unlike `<NetworkMonitorPageHeader>`)
 * because the analytics surface uses the global accent token
 * `var(--primary)` everywhere.
 */

import type { ReactNode } from 'react';

interface DataPageHeaderProps {
  /** Icon rendered before the title (e.g. `<Icons.PieChart size={28} />`). */
  icon: ReactNode;
  /** Page title — renders as the page `<h1>`. */
  title: string;
  /** Subtitle paragraph under the title. Optional. */
  subtitle?: ReactNode;
  /** Optional slot to the right of the title — used by Risk Analysis
   *  for its tab switcher. Renders inside the same flex row as the
   *  title block. */
  extras?: ReactNode;
}

export function DataPageHeader({
  icon,
  title,
  subtitle,
  extras,
}: DataPageHeaderProps) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 700,
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {icon}
            {title}
          </h1>
          {subtitle && (
            <p style={{ color: 'var(--muted)', fontSize: '14px', margin: 0 }}>
              {subtitle}
            </p>
          )}
        </div>
        {extras}
      </div>
    </div>
  );
}
